import {
  createSlice,
  createEntityAdapter,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import {
  AssigneeOption,
  ISalesTeam,
  ISalesTeamPosition,
  IUserPosition,
} from './typings'
import {
  getSalesTeamList,
  createSalesTeam,
  getSalesTeam,
  updateSalesTeam,
  createSalesTeamPosition,
  getSalesTeamUsers,
  deleteSalesTeam,
  deleteSalesTeamPosition,
  getSalesTeamsUsers,
  getCurrentUserSalesTeams,
  getSalesTeamsUsersByTeamStructure,
} from './api'
import { isAPIValidationError } from '../services/crud'
import { updateSalesTeamPosition } from './api'
import { adminRoleName } from '@user/components/Settings/Users/UpdateUser/constants'

const salesTeamAdapter = createEntityAdapter<ISalesTeam>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
})

const positionAdapter = createEntityAdapter<ISalesTeamPosition>()

const userAdapter = createEntityAdapter<IUserPosition>({
  selectId: (x) => x.userId,
})

const salesTeamsUsersAdapter = createEntityAdapter<AssigneeOption>()

export const initialState = {
  teams: salesTeamAdapter.getInitialState(),
  positions: positionAdapter.getInitialState(),
  users: userAdapter.getInitialState(),
  salesTeamsUsers: salesTeamsUsersAdapter.getInitialState(),
}

export const salesTeam = createSlice({
  name: 'salesTeam',
  initialState,
  reducers: {
    addUserPosition: (state, { payload }: PayloadAction<IUserPosition>) => {
      userAdapter.addOne(state.users, payload)
    },
    removeUserPosition: (state, { payload }: PayloadAction<string>) => {
      userAdapter.removeOne(state.users, payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSalesTeamList.fulfilled, (state, { payload }) => {
      salesTeamAdapter.setAll(state.teams, payload)

      const positions: ISalesTeamPosition[] = []
      payload.forEach((t) => positions.push(...t.positions))
      positionAdapter.setAll(state.positions, positions)
    })
    builder.addCase(getCurrentUserSalesTeams.fulfilled, (state, action) => {
      salesTeamAdapter.setAll(state.teams, action.payload)
    })
    builder.addCase(createSalesTeam.fulfilled, (state, { payload }) => {
      salesTeamAdapter.addOne(state.teams, payload)
      positionAdapter.addMany(state.positions, payload.positions)
    })
    builder.addCase(deleteSalesTeam.fulfilled, (state, { payload }) => {
      salesTeamAdapter.removeOne(state.teams, payload)
    })
    builder.addCase(getSalesTeam.fulfilled, (state, { payload }) => {
      salesTeamAdapter.upsertOne(state.teams, payload)
      positionAdapter.upsertMany(state.positions, payload.positions)
    })
    builder.addCase(updateSalesTeam.fulfilled, (state, { payload }) => {
      salesTeamAdapter.upsertOne(state.teams, payload)
    })
    builder.addCase(createSalesTeamPosition.fulfilled, (state, { payload }) => {
      positionAdapter.addOne(state.positions, payload)
    })
    builder.addCase(createSalesTeamPosition.rejected, (state, { payload }) => {
      if (isAPIValidationError(payload) && !!payload.errors) {
        const errors = payload.errors
        const map = new Map(
          Object.keys(payload.errors).map((k) => [k.toLowerCase(), errors[k]]),
        )
        const userErrors = map.get('usersalreadymapped')
        if (userErrors) {
          const invalidUsers: Update<IUserPosition>[] = userErrors.map((e) => ({
            id: e,
            changes: { hasError: true },
          }))
          userAdapter.updateMany(state.users, invalidUsers)
        }
      }
    })
    builder.addCase(updateSalesTeamPosition.fulfilled, (state, { payload }) => {
      positionAdapter.upsertMany(state.positions, payload)
    })
    builder.addCase(deleteSalesTeamPosition.fulfilled, (state, { payload }) => {
      positionAdapter.removeOne(state.positions, payload)
    })

    builder.addCase(getSalesTeamUsers.fulfilled, (state, { payload }) => {
      userAdapter.setAll(state.users, payload)
    })
    builder.addCase(getSalesTeamsUsers.fulfilled, (state, { payload }) => {
      salesTeamsUsersAdapter.setAll(state.salesTeamsUsers, payload)
    })
    builder.addCase(
      getSalesTeamsUsersByTeamStructure.fulfilled,
      (state, { payload }) => {
        salesTeamsUsersAdapter.setAll(state.salesTeamsUsers, payload)
      },
    )
  },
})

export default salesTeam.reducer

export const { addUserPosition, removeUserPosition } = salesTeam.actions

export const salesTeamAdapterSelectors =
  salesTeamAdapter.getSelectors<StoreData>((state) => state.salesTeam.teams)

export const {
  selectById: selectSalesTeamById,
  selectAll: selectAllSalesTeams,
} = salesTeamAdapterSelectors

export const salesTeamsForCurrentUser = (state: StoreData) =>
  selectAllSalesTeams(state).filter((t) => t.hasAccess === true)

export const positionAdapterSelectors = positionAdapter.getSelectors<StoreData>(
  (state) => state.salesTeam.positions,
)

export const { selectById: selectPositionById, selectAll: selectAllPositions } =
  positionAdapterSelectors

export const aggregatePositionsByTeamId = (
  salesTeamId: number,
  positions: ISalesTeamPosition[],
) =>
  positions.filter(
    (p) =>
      p.salesTeamId == salesTeamId &&
      !(p.systemCreated && p.title == adminRoleName),
  )

const userAdapterSelectors = userAdapter.getSelectors<StoreData>(
  (state) => state.salesTeam.users,
)

export const { selectAll: selectAllSalesTeamUsers } = userAdapterSelectors

const salesTeamsUsersAdapterSelectors =
  salesTeamsUsersAdapter.getSelectors<StoreData>(
    (state) => state.salesTeam.salesTeamsUsers,
  )

export const { selectAll: selectAllSalesTeamsUsers } =
  salesTeamsUsersAdapterSelectors

export const aggregateUsersByPositionId = (
  positionId: number,
  users: IUserPosition[],
) => users.filter((u) => u.positionId == positionId)

export const salesTeamUsersValid = (state: StoreData) =>
  selectAllSalesTeamUsers(state).filter((u) => !u.hasError).length === 0

export const aggregateTeamsByBranchId = (
  branchId: string,
  teams: ISalesTeam[],
) => teams.filter((p) => p.branchId == branchId)

export const aggregateTeamsByBranchName = (
  branchName: string,
  teams: ISalesTeam[],
) => teams.filter((p) => p.branchName == branchName)
